import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Toast } from "@tento-ui/components/toast/toast";
import { InstanceUrl } from "@tento-core/instance-url";
import { CalendarProviders } from "../calendar-providers";
import { ApiKeyPage } from "../api-key.page";
import { OAuthPage } from "../oauth.page";
import { OAuthPopup } from "../oauth-popup";
import { WhoopSuccessPage } from "../whoop-success.page";

@component({
  name: "lona-integrations-selector",
})
export class IntegrationsSelector extends LonaWebComponent {
  static makeWith(
    integrations: IntegrationCard.Prop[] = IntegrationsSelector.DEFAULT_INTEGRATIONS,
  ): IntegrationsSelector {
    return IntegrationsSelector.make().bind(integrations);
  }

  bind(
    integrations: IntegrationCard.Prop[] = IntegrationsSelector.DEFAULT_INTEGRATIONS,
  ): IntegrationsSelector {
    this.clearChildren();
    for (const integration of integrations) {
      this.appendChild(IntegrationCard.makeWith(integration));
    }
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        padding: 20px;
        min-height: 400px;
        justify-content: inherit;
      }

      slot::slotted(*) {
        margin-bottom: 8px;
      }

      slot::slotted(*:last-child) {
        margin-bottom: 0px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <slot></slot>
    </std-col>
  `;
}

export namespace IntegrationsSelector {
  /** One card per registered calendar provider. */
  export function calendarIntegrations(anyLinked?: boolean): IntegrationCard.Prop[] {
    return CalendarProviders.list().map((d) => ({
      icon: d.icon,
      title: d.label,
      description: d.connectDescription(anyLinked ?? false),
      onSelect: () => d.connect(),
    }));
  }

  export const GARMIN: IntegrationCard.Prop = {
    icon: "/@/static/img/integrations/garmin-64-icon.svg",
    title: "Garmin",
    description: "Sync your Garmin health data to view it in Lona",
    onSelect: async () => {
      OAuthPage.show(OAuthPage.GARMIN);
    },
  };

  export function openWhoopAuthPopup(onSuccess?: Option<() => void>) {
    const clientId = "db13cdb7-3ff0-4a49-81f5-0746b2d12d56";
    const redirectUri = encodeURIComponent(
      InstanceUrl.root("/api/user/connections/whoop/redirect"),
    );
    const state = "RANDOM_STRING";
    const scope =
      "read:recovery read:cycles read:sleep read:workout read:profile read:body_measurement";

    const authUrl =
      "https://api.prod.whoop.com/oauth/oauth2/auth" +
      `?response_type=code&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;

    const opened = OAuthPopup.open({
      authUrl,
      source: "whoop",
      onSuccess: () => {
        onSuccess?.();
        WhoopSuccessPage.reveal(() => {});
      },
      onFailure: null,
      onDismiss: null,
    });
    if (!opened) {
      Toast.error("Something went wrong");
    }
  }

  export const WHOOP: IntegrationCard.Prop = {
    icon: "/@/static/img/integrations/whoop.svg",
    title: "Whoop",
    description: "Sync your Whoop health data to view it in Lona",
    onSelect: async () => {
      openWhoopAuthPopup(() => Toast.info("Whoop connected"));
    },
  };
  export const LINEAR: IntegrationCard.Prop = {
    icon: "/@/static/img/integrations/garmin-64-icon.svg",
    title: "Linear",
    description: "Sync your tasks and schedule it automatically in Lona",
    onSelect: async () => {
      Toast.todo();
    },
  };

  export const GEMINI: IntegrationCard.Prop = {
    icon: "/@/static/img/integrations/gemini-64-icon.svg",
    title: "Gemini",
    description: "Use your own Gemini API key for AI chat",
    onSelect: async () => {
      ApiKeyPage.show(ApiKeyPage.GEMINI);
    },
  };

  export const CLAUDE: IntegrationCard.Prop = {
    icon: "/@/static/img/integrations/claude-64-icon.svg",
    title: "Claude",
    description: "Use your own Claude API key for AI chat",
    onSelect: async () => {
      ApiKeyPage.show(ApiKeyPage.CLAUDE);
    },
  };

  export const OPENAI: IntegrationCard.Prop = {
    icon: "/@/static/img/integrations/openai-64-icon.svg",
    title: "OpenAI",
    description: "Use your own OpenAI API key for AI chat",
    onSelect: async () => {
      ApiKeyPage.show(ApiKeyPage.OPENAI);
    },
  };

  export const LLM_INTEGRATIONS: IntegrationCard.Prop[] = [
    GEMINI,
    CLAUDE,
    OPENAI,
  ];
  export const OTHER_INTEGRATIONS: IntegrationCard.Prop[] = [
    GARMIN,
    // LINEAR,
    WHOOP,
    ...LLM_INTEGRATIONS,
  ];
  export function defaultIntegrations(anyCalendarLinked?: boolean): IntegrationCard.Prop[] {
    return [...calendarIntegrations(anyCalendarLinked), ...OTHER_INTEGRATIONS];
  }

  // Static defaults for backward compatibility
  export const CALENDAR_INTEGRATIONS: IntegrationCard.Prop[] = calendarIntegrations();
  export const DEFAULT_INTEGRATIONS: IntegrationCard.Prop[] = defaultIntegrations();
}

@component({
  name: "integration-card",
})
export class IntegrationCard extends LonaWebComponent {
  static makeWith(integration: IntegrationCard.Prop): IntegrationCard {
    return IntegrationCard.make().bind(integration);
  }

  bind(integration: IntegrationCard.Prop): IntegrationCard {
    this.$("icon").style.backgroundImage = `url("${integration.icon}")`;
    this.$("title").textContent = integration.title;
    this.$("description").textContent = integration.description;
    this.addPointerEvent({
      onClick: async () => await integration.onSelect(),
    });
    return this;
  }

  static $styles: Style.Compat[] = [
    Button.$style,
    css`
      #root {
        border-radius: 8px;
        border: 1px solid var(--divider);
        padding: 12px 16px;
        cursor: pointer;
      }

      #root:hover {
        background: var(--bg-hover);
      }

      #title {
        --margin-bottom: 8px;
      }

      #icon {
        height: 32px;
        width: 32px;
        margin-right: 10px;
        background-size: contain;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <div id="icon"></div>
      <std-col>
        <h2 id="title" class="h6"></h2>
        <p id="description" class="small"></p>
      </std-col>
      <std-spacer></std-spacer>
      <slot id="accessory"></slot>
    </std-row>
  `;
}

export namespace IntegrationCard {
  export type Prop = {
    icon: string;
    title: string;
    description: string;
    onSelect: () => Promise<void>;
  };
}
